/**
 * Registro de la respuesta de una entidad — el disparador de la cascada.
 *
 * `NEG-8`: una respuesta concluyente cierra el requerimiento. Si llega antes
 * del último paso, conserva el avance y termina anticipadamente; si el paso
 * pendiente ya es el cierre, respuesta y cierre se registran juntos.
 *
 * `NEG-3`: la evidencia es obligatoria, sin excepción. No es una validación de
 * formulario: es el requisito de que una decisión con efectos legales esté
 * respaldada por el documento que la sostiene.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mpfn/ui'
import { Callout, DatePicker, FileUploadField, FormField } from '@mpfn/patterns'
import { useRegistrarRespuesta } from '../api/consultas.js'
import { useCatalogos } from '../api/catalogos.js'
import type { Consulta, Pronunciamiento } from '../api/tipos.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { hoy } from '../lib/formato.js'

/**
 * Qué desenlace produce cada pronunciamiento, y si es concluyente.
 *
 * `no_competente` NO es concluyente: no dice que el documento sea falso, dice
 * que se preguntó en la puerta equivocada. Habilita corregir la entidad y
 * reabrir sin consumir ordinal (`NEG-11`).
 */
const CONCLUYENTES: ReadonlySet<Pronunciamiento> = new Set<Pronunciamiento>(['confirma', 'niega'])

interface Props {
  consulta: Consulta
  /** Hermanas vivas que se abandonarían si este pronunciamiento concluye. */
  hermanasVivas: number
  /** El siguiente paso de esta consulta es el cierre formal del canal. */
  pasoCierrePendiente: boolean
  /** El actor puede registrar ese paso además de la respuesta. */
  puedeRegistrarPasoCierre: boolean
  abierto: boolean
  onCambio: (abierto: boolean) => void
}

export function DialogoRespuesta({
  consulta,
  hermanasVivas,
  pasoCierrePendiente,
  puedeRegistrarPasoCierre,
  abierto,
  onCambio,
}: Props) {
  const catalogos = useCatalogos()
  const registrar = useRegistrarRespuesta()

  const [fechaRecepcion, setFechaRecepcion] = useState(hoy())
  const [pronunciamiento, setPronunciamiento] = useState<Pronunciamiento | ''>('')
  const [referencia, setReferencia] = useState('')
  const [observacion, setObservacion] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)

  useEffect(() => {
    if (!abierto) {
      setFechaRecepcion(hoy())
      setPronunciamiento('')
      setReferencia('')
      setObservacion('')
      setArchivo(null)
    }
  }, [abierto])

  const concluye = pronunciamiento !== '' && CONCLUYENTES.has(pronunciamiento)
  const niega = pronunciamiento === 'niega'
  const finalizaPasoCierre = concluye && pasoCierrePendiente
  const faltaPermisoCierre = finalizaPasoCierre && !puedeRegistrarPasoCierre
  const listo =
    pronunciamiento !== '' &&
    observacion.trim() !== '' &&
    archivo !== null &&
    !faltaPermisoCierre

  return (
    <Dialog open={abierto} onOpenChange={onCambio}>
      <DialogContent>
        <DialogTitle>Registrar la respuesta</DialogTitle>
        <DialogDescription>
          {consulta.canalEtiqueta} · {consulta.destinoTexto}
        </DialogDescription>

        <form
          className="sigfisc-formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            // `listo` ya garantiza pronunciamiento y archivo; el compilador lo
            // sabe y estrecha los tipos, así que no hace falta repetirlo.
            if (!listo) return
            registrar.mutate(
              {
                consultaId: consulta.id,
                fechaRecepcion,
                pronunciamiento,
                ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
                observacion: observacion.trim(),
                archivo,
              },
              {
                onSuccess: (datos) => {
                  const detalles: string[] = []
                  if (datos.pasoCierreRegistrado) detalles.push('Se registró también el paso de cierre.')
                  if (datos.consultasAbandonadas > 0) {
                    detalles.push(
                      `Se abandonaron ${plural(datos.consultasAbandonadas, 'consulta', 'consultas')} del mismo requerimiento.`,
                    )
                  }
                  avisarExito(
                    datos.pasoCierreRegistrado
                      ? 'Consulta finalizada con respuesta'
                      : datos.cerroRequerimiento
                        ? 'Requerimiento cerrado'
                        : 'Respuesta registrada',
                    detalles.length > 0 ? detalles.join(' ') : undefined,
                  )
                  onCambio(false)
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo registrar la respuesta')
                },
              },
            )
          }}
        >
          <FormField id="respuesta-fecha" label="Fecha de recepción" required>
            <DatePicker
              id="respuesta-fecha"
              value={fechaRecepcion}
              onChange={setFechaRecepcion}
              yearRange={{ from: Number(hoy().slice(0, 4)) - 1, to: Number(hoy().slice(0, 4)) }}
            />
          </FormField>

          <FormField id="respuesta-pronunciamiento" label="Pronunciamiento de la entidad" required>
            <Select
              value={pronunciamiento}
              onValueChange={(valor) => {
                setPronunciamiento(valor as Pronunciamiento)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Qué respondió la entidad" />
              </SelectTrigger>
              <SelectContent>
                {catalogos.pronunciamientos.map((opcion) => (
                  <SelectItem key={opcion.codigo} value={opcion.codigo}>
                    {opcion.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {niega && (
            <Callout tone="critical" title="La entidad niega la autenticidad">
              Es un hallazgo sustantivo, no un fallo administrativo: el documento quedará como
              irregular y habilitará la derivación inmediata al OCI.
            </Callout>
          )}

          {finalizaPasoCierre && (
            <Callout tone="info" title="Se registrará el cierre formal">
              La respuesta y el paso {String(consulta.totalPasos)} de {String(consulta.totalPasos)}
              se guardarán en una sola operación.
            </Callout>
          )}

          {faltaPermisoCierre && (
            <Callout tone="warning" title="No puede registrar el cierre">
              Puede registrar respuestas intermedias o no concluyentes, pero esta respuesta también
              completaría el último paso y requiere el permiso correspondiente.
            </Callout>
          )}

          {concluye && !pasoCierrePendiente && (
            <Callout tone="info" title="La respuesta finaliza anticipadamente la consulta">
              Se conservará el avance alcanzado ({String(consulta.paso)}/{String(consulta.totalPasos)}).
              Los pasos posteriores no se completarán artificialmente.
            </Callout>
          )}

          {concluye && hermanasVivas > 0 && (
            <Callout tone="warning" title="Se abandonarán las consultas hermanas">
              Este pronunciamiento cierra el requerimiento, así que{' '}
              {plural(hermanasVivas, 'consulta abierta por otro canal dejará', 'consultas abiertas por otros canales dejarán')}{' '}
              de esperarse. Los pasos que ya recorrieron se conservan.
            </Callout>
          )}

          {pronunciamiento === 'no_competente' && (
            <Callout tone="info" title="No cierra el requerimiento">
              Se preguntó a quien no corresponde. Puede corregirse la entidad y volver a consultar
              sin consumir un requerimiento.
            </Callout>
          )}

          <FormField id="respuesta-referencia" label="Referencia del documento de respuesta">
            <Input
              value={referencia}
              placeholder="Oficio 245-2026-UNHEVAL/SG"
              onChange={(evento) => {
                setReferencia(evento.target.value)
              }}
            />
          </FormField>

          <FormField id="respuesta-observacion" label="Observación" required>
            <Textarea
              rows={3}
              value={observacion}
              onChange={(evento) => {
                setObservacion(evento.target.value)
              }}
            />
          </FormField>

          <FormField
            id="respuesta-evidencia"
            label="Evidencia de la respuesta"
            required
            hint="Obligatoria sin excepción: es la prueba con la que se cierra."
          >
            <FileUploadField
              id="respuesta-evidencia"
              value={archivo}
              onChange={setArchivo}
              accept=".pdf,image/*"
              acceptLabel="PDF o imagen"
              maxSizeMb={20}
              variant="compact"
              onError={(mensaje) => {
                avisarError(new Error(mensaje), 'El archivo no es válido')
              }}
            />
          </FormField>

          <div className="sigfisc-dialogo__acciones">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant={niega ? 'destructive' : 'primary'}
              type="submit"
              loading={registrar.isPending}
              disabled={!listo}
            >
              {finalizaPasoCierre
                ? 'Finalizar con respuesta'
                : concluye
                  ? 'Registrar respuesta y finalizar'
                  : 'Registrar la respuesta'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
